import type { HintStrategy, LangCode, UserLanguage, UserRole, UserTeam } from "@scoach/types";
import { Button, useToast } from "@scoach/ui";
import { Logout } from "@scoach/ui/icons";

type ComparisonMode = "aggressive" | "conservative";
import {
  CUSTOM_QUESTION_PRESETS,
  DEFAULT_CUSTOM_QUESTIONS,
  LANGUAGES,
} from "@scoach/types";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { driveApi } from "../../../lib/driveApi.ts";
import { signOut } from "../../../lib/firebase.ts";
import { getDriveAuthCode } from "../../../lib/googleToken.ts";
import { authApi } from "../../auth/api.ts";
import { useAuthStore } from "../../auth/store.ts";
import { useAvailableRoles } from "../../../lib/roles.ts";
import { CustomQuestionsEditor } from "./CustomQuestionsEditor.tsx";
import { SuggestedQuestionsBanner } from "./SuggestedQuestionsBanner.tsx";

type TabId = "profile" | "language" | "coaching" | "summary";

const TEAMS: UserTeam[] = [
  "EMEA Cloud Sales",
  "NAMER Cloud Sales",
  "APAC Cloud Sales",
  "LATAM Cloud Sales",
  "Strategic Accounts",
];

const LANGS: { value: UserLanguage; label: string; flag: string }[] = [
  { value: "en", label: "English", flag: "\u{1F1FA}\u{1F1F8}" },
  { value: "he", label: "עברית", flag: "\u{1F1EE}\u{1F1F1}" },
  { value: "es", label: "Español", flag: "\u{1F1EA}\u{1F1F8}" },
  { value: "fr", label: "Français", flag: "\u{1F1EB}\u{1F1F7}" },
  { value: "de", label: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}" },
  { value: "it", label: "Italiano", flag: "\u{1F1EE}\u{1F1F9}" },
  { value: "nl", label: "Nederlands", flag: "\u{1F1F3}\u{1F1F1}" },
  { value: "pt-BR", label: "Português (Brasil)", flag: "\u{1F1E7}\u{1F1F7}" },
  { value: "ru", label: "Русский", flag: "\u{1F1F7}\u{1F1FA}" },
  { value: "tr", label: "Türkçe", flag: "\u{1F1F9}\u{1F1F7}" },
  { value: "ja", label: "日本語", flag: "\u{1F1EF}\u{1F1F5}" },
  { value: "ko", label: "한국어", flag: "\u{1F1F0}\u{1F1F7}" },
  { value: "zh-CN", label: "中文 (简体)", flag: "\u{1F1E8}\u{1F1F3}" },
  { value: "hi", label: "हिन्दी", flag: "\u{1F1EE}\u{1F1F3}" },
  { value: "ar", label: "العربية", flag: "\u{1F1F8}\u{1F1E6}" },
];

const TAB_IDS: TabId[] = ["profile", "language", "coaching", "summary"];

export function SettingsScreen({ initialTab }: { initialTab?: string } = {}) {
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);

  const nav = useNavigate();
  const toast = useToast();

  const [tab, setTab] = useState<TabId>(
    initialTab && (TAB_IDS as string[]).includes(initialTab) ? (initialTab as TabId) : "profile",
  );
  const [draftName, setDraftName] = useState(user?.name ?? "");
  const availableRoles = useAvailableRoles();
  const [draftRole, setDraftRole] = useState<UserRole>(user?.role ?? "Sales / AE");
  const roleOptions = useMemo(() => {
    if (draftRole && !availableRoles.includes(draftRole)) {
      return [draftRole, ...availableRoles];
    }
    return availableRoles;
  }, [availableRoles, draftRole]);
  const [draftTeam, setDraftTeam] = useState<UserTeam>(user?.team ?? "EMEA Cloud Sales");
  const [language, setLanguage] = useState<UserLanguage>(user?.settings.language ?? "en");
  const [hintStrategy, setHintStrategy] = useState<HintStrategy>(
    user?.settings.hintStrategy ?? "live",
  );
  const [assistanceLanguage, setAssistanceLanguage] = useState<"auto" | LangCode>(
    user?.settings.assistanceLanguage ?? "auto",
  );
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>(
    user?.settings.comparisonMode ?? "aggressive",
  );
  const [autoSummary, setAutoSummary] = useState(user?.settings.autoSummary ?? true);
  const [quietByDefault, setQuietByDefault] = useState(user?.settings.quietByDefault ?? false);
  const [keepTranscript, setKeepTranscript] = useState(user?.settings.keepTranscript !== false);
  const [onboardingAgentEnabled, setOnboardingAgentEnabled] = useState(
    user?.settings.onboardingAgentEnabled !== false,
  );
  const [customQuestions, setCustomQuestions] = useState<string[]>(
    user?.settings.customQuestions ?? DEFAULT_CUSTOM_QUESTIONS,
  );
  // Per-role / per-meeting-type question sets the user has edited. "" = the default set
  // above; any other key edits customQuestionSets[key] (seeded from the preset).
  const [qSetKey, setQSetKey] = useState<string>("");
  const [customQuestionSets, setCustomQuestionSets] = useState<Record<string, string[]>>(
    user?.settings.customQuestionSets ?? {},
  );
  const activeQuestions =
    qSetKey === ""
      ? customQuestions
      : (customQuestionSets[qSetKey] ?? CUSTOM_QUESTION_PRESETS[qSetKey] ?? []);
  const setActiveQuestions = (next: string[]) => {
    if (qSetKey === "") setCustomQuestions(next);
    else setCustomQuestionSets((prev) => ({ ...prev, [qSetKey]: next }));
  };
  const [customSummaryEnabled, setCustomSummaryEnabled] = useState(
    user?.settings.customSummaryEnabled !== false,
  );

  // Google Drive export: connection status + target folder name. Status is loaded from
  // the server (source of truth for the stored refresh token); the folder name is a
  // normal setting saved via "Save changes".
  const [driveFolderName, setDriveFolderName] = useState(
    user?.settings.driveFolderName ?? "SellSynq Meeting Exports",
  );
  const [driveConnected, setDriveConnected] = useState(false);
  const [driveBusy, setDriveBusy] = useState(false);
  useEffect(() => {
    driveApi
      .status()
      .then((s) => {
        setDriveConnected(s.connected);
        if (s.folderName) setDriveFolderName(s.folderName);
      })
      .catch(() => {});
  }, []);

  async function connectDrive() {
    setDriveBusy(true);
    try {
      const code = await getDriveAuthCode();
      const s = await driveApi.connect(code);
      setDriveConnected(s.connected);
      toast.push({ tone: "success", message: "Google Drive connected" });
    } catch (err) {
      toast.push({
        tone: "error",
        message: `Couldn't connect Google Drive — allow the popup and try again. (${(err as Error).message})`,
      });
    } finally {
      setDriveBusy(false);
    }
  }

  async function disconnectDrive() {
    setDriveBusy(true);
    try {
      await driveApi.disconnect();
      setDriveConnected(false);
      toast.push({ tone: "info", message: "Google Drive disconnected" });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setDriveBusy(false);
    }
  }
  // Manage user-created question templates (named sets beyond the built-in presets).
  const [qMode, setQMode] = useState<"idle" | "new" | "rename">("idle");
  const [qNameDraft, setQNameDraft] = useState("");
  // User templates = keys the user added that aren't built-in presets.
  const userTemplateKeys = Object.keys(customQuestionSets)
    .filter((k) => !CUSTOM_QUESTION_PRESETS[k])
    .sort();
  const isUserTemplate = qSetKey !== "" && !CUSTOM_QUESTION_PRESETS[qSetKey];

  function createTemplate() {
    const name = qNameDraft.trim();
    if (!name) return;
    if (CUSTOM_QUESTION_PRESETS[name] || customQuestionSets[name]) {
      toast.push({ tone: "error", message: "A question set with that name already exists." });
      return;
    }
    setCustomQuestionSets((prev) => ({ ...prev, [name]: [""] }));
    setQSetKey(name);
    setQMode("idle");
    setQNameDraft("");
  }

  function renameTemplate() {
    const name = qNameDraft.trim();
    if (!name || name === qSetKey) {
      setQMode("idle");
      return;
    }
    if (CUSTOM_QUESTION_PRESETS[name] || customQuestionSets[name]) {
      toast.push({ tone: "error", message: "A question set with that name already exists." });
      return;
    }
    setCustomQuestionSets((prev) => {
      const next = { ...prev };
      next[name] = next[qSetKey] ?? [];
      delete next[qSetKey];
      return next;
    });
    setQSetKey(name);
    setQMode("idle");
    setQNameDraft("");
  }

  function deleteTemplate() {
    setCustomQuestionSets((prev) => {
      const next = { ...prev };
      delete next[qSetKey];
      return next;
    });
    setQSetKey("");
    setQMode("idle");
  }

  const [saving, setSaving] = useState(false);

  async function saveAll() {
    setSaving(true);
    try {
      const cleanedQuestions = customQuestions.map((q) => q.trim()).filter(Boolean);
      const cleanedSets: Record<string, string[]> = {};
      for (const [k, v] of Object.entries(customQuestionSets)) {
        cleanedSets[k] = v.map((q) => q.trim()).filter(Boolean);
      }
      await Promise.all([
        authApi.updateProfile({ name: draftName, role: draftRole, team: draftTeam }),
        authApi.updateSettings({
          language,
          hintStrategy,
          assistanceLanguage,
          comparisonMode,
          autoSummary,
          quietByDefault,
          keepTranscript,
          onboardingAgentEnabled,
          customQuestions: cleanedQuestions,
          customQuestionSets: cleanedSets,
          customSummaryEnabled,
          driveFolderName: driveFolderName.trim() || "SellSynq Meeting Exports",
        }),
      ]);
      const next = user
        ? {
            ...user,
            name: draftName,
            role: draftRole,
            team: draftTeam,
            settings: {
              ...user.settings,
              language,
              hintStrategy,
              assistanceLanguage,
              comparisonMode,
              autoSummary,
              quietByDefault,
              keepTranscript,
              onboardingAgentEnabled,
              customQuestions: cleanedQuestions,
              customQuestionSets: cleanedSets,
              customSummaryEnabled,
              driveFolderName: driveFolderName.trim() || "SellSynq Meeting Exports",
            },
          }
        : null;
      if (next) setUser(next);
      toast.push({ tone: "success", message: "Saved" });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    try {
      await Promise.resolve(authApi.signOut?.());
    } catch {}
    try {
      await signOut();
    } catch {}
    clear();
    nav({ to: "/signin" });
  }

  const [exporting, setExporting] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  async function exportMyData() {
    setExporting(true);
    try {
      const data = await authApi.exportData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sally-my-data.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message || "Export failed" });
    } finally {
      setExporting(false);
    }
  }

  async function deleteMyAccount() {
    if (
      !window.confirm(
        "Permanently delete your account and ALL your meetings, transcripts and summaries? This cannot be undone.",
      )
    )
      return;
    setDeletingAccount(true);
    try {
      await authApi.deleteAccount();
      await handleSignOut();
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message || "Delete failed" });
      setDeletingAccount(false);
    }
  }

  const meetingLangs =
    language === "en"
      ? "English"
      : `English + ${LANGS.find((l) => l.value === language)?.label ?? language}`;

  return (
    <div className="settings-page">
      <header className="settings-top">
        <button type="button" className="ghost-btn" onClick={() => nav({ to: "/dashboard" })}>
          &larr; Back
        </button>
        <div className="settings-title-wrap">
          <h1 className="settings-title">Settings</h1>
        </div>
        <div className="settings-top-actions">
          <Button variant="primary" onClick={saveAll} loading={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </header>

      <div className="settings-body">
        <nav className="settings-nav">
          <ul>
            {[
              { id: "profile" as TabId, label: "Profile" },
                            { id: "language" as TabId, label: "Language" },
              { id: "coaching" as TabId, label: "Coaching" },
              { id: "summary" as TabId, label: "Custom summary" },
            ].map((item) => (
              <li
                key={item.id}
                className={tab === item.id ? "on" : ""}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </li>
            ))}
          </ul>
          <button type="button" className="signout-btn" onClick={handleSignOut}>
            <Logout size={16} /> Sign out
          </button>
        </nav>

        <main className="settings-main">
          {tab === "profile" && (
            <>
              <div className="set-section">
                <h2>Profile</h2>
                <div className="set-grid">
                  <div className="set-field">
                    <div className="set-field-top">
                      <span className="set-field-label">Full name</span>
                    </div>
                    <input value={draftName} onChange={(e) => setDraftName(e.target.value)} />
                  </div>
                  <div className="set-field">
                    <div className="set-field-top">
                      <span className="set-field-label">Email</span>
                      <span className="set-field-hint">Synced from your Google account.</span>
                    </div>
                    <input className="ro" value={user?.email ?? ""} disabled />
                  </div>
                  <div className="set-field">
                    <div className="set-field-top">
                      <span className="set-field-label">Role</span>
                    </div>
                    <select
                      value={draftRole}
                      onChange={(e) => setDraftRole(e.target.value as UserRole)}
                    >
                      {roleOptions.map((r: string) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="set-field">
                    <div className="set-field-top">
                      <span className="set-field-label">Team</span>
                    </div>
                    <select
                      value={draftTeam}
                      onChange={(e) => setDraftTeam(e.target.value as UserTeam)}
                    >
                      {TEAMS.map((tm) => (
                        <option key={tm} value={tm}>
                          {tm}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="set-section">
                <h2>Google Drive export</h2>
                <p className="set-section-sub">
                  Save any meeting's transcript + summary as a Markdown file to your Google Drive
                  (from the meeting's summary page). Great for reasoning over your notes in other
                  tools. Files land in the folder below in your own Drive.
                </p>
                <div className="key-status">
                  <span className={`key-dot ${driveConnected ? "ok" : "warn"}`} />
                  <div>
                    {driveConnected
                      ? "Connected — exports go straight to your Drive."
                      : "Not connected yet. Connect once, then export seamlessly."}
                  </div>
                </div>
                <div className="set-grid">
                  <div className="set-field">
                    <div className="set-field-top">
                      <span className="set-field-label">Export folder name</span>
                      <span className="set-field-hint">
                        Created in your Drive if it doesn't exist. Saved with “Save changes”.
                      </span>
                    </div>
                    <input
                      value={driveFolderName}
                      onChange={(e) => setDriveFolderName(e.target.value)}
                      placeholder="SellSynq Meeting Exports"
                    />
                  </div>
                </div>
                <div className="key-actions">
                  {driveConnected ? (
                    <Button variant="ghost" onClick={disconnectDrive} loading={driveBusy}>
                      Disconnect Drive
                    </Button>
                  ) : (
                    <Button variant="primary" onClick={connectDrive} loading={driveBusy}>
                      {driveBusy ? "Connecting…" : "Connect Google Drive"}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {tab === "language" && (
            <div className="set-section">
              <h2>Default meeting language</h2>
              <p className="set-section-sub">
                Choose the primary language for your meetings. English is always included, and
                transcription automatically detects the language actually spoken — so French,
                German, and any other supported language are transcribed in their own language, not
                forced into one.
              </p>
              <div className="lang-grid">
                {LANGS.map((l) => (
                  <button
                    key={l.value}
                    type="button"
                    className={`lang-card ${language === l.value ? "on" : ""}`}
                    onClick={() => setLanguage(l.value)}
                  >
                    <span className="lang-flag" aria-hidden>
                      {l.flag}
                    </span>
                    <div className="lang-meta">
                      <div className="lang-label">{l.label}</div>
                      <div className="lang-code">{l.value}</div>
                    </div>
                    {language === l.value && <span className="lang-check">{"✓"}</span>}
                  </button>
                ))}
              </div>
              <div className="set-callout" style={{ marginTop: 14 }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <div>
                  <strong>New meetings will default to your selected language.</strong>
                  <div style={{ marginTop: 4 }}>
                    Meeting language: <strong>{meetingLangs}</strong>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "coaching" && (
            <div className="set-section">
              <h2>Coaching behavior</h2>
              <div className="set-field">
                <div className="set-field-top">
                  <span className="set-field-label">Hint strategy</span>
                </div>
                <div className="seg-lg">
                  {(
                    [
                      { value: "live", label: "Live" },
                      { value: "balanced", label: "Balanced" },
                      { value: "focused", label: "Focused" },
                    ] as { value: HintStrategy; label: string }[]
                  ).map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      className={hintStrategy === s.value ? "on" : ""}
                      onClick={() => setHintStrategy(s.value)}
                    >
                      <span className="seg-label">{s.label}</span>
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-2)" }}>
                  {hintStrategy === "live"
                    ? "Frequent hints as the conversation moves — the most, but you may need to scroll. Screen-share insights all show."
                    : hintStrategy === "balanced"
                      ? "Slower, calmer hints — only when they clearly help. Screen-share insights only for competitors or pricing."
                      : "Only the critical moments — a competitor, a real objection, a strong buying signal. Screen-share insights only for competitors."}
                </div>
                <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--text-2)", opacity: 0.8 }}>
                  You can also switch this live from the meeting header — your last choice sticks.
                </div>
              </div>
              <div className="set-field">
                <div className="set-field-top">
                  <span className="set-field-label">Hints &amp; answers language</span>
                </div>
                <select
                  className="setup-select"
                  value={assistanceLanguage}
                  onChange={(e) => setAssistanceLanguage(e.target.value as "auto" | LangCode)}
                >
                  <option value="auto">Same as the meeting (spoken) language</option>
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label} ({l.labelEn})
                    </option>
                  ))}
                </select>
                <div style={{ marginTop: 6, fontSize: 12.5, color: "var(--text-2)", opacity: 0.8 }}>
                  The language SellSynq writes hints &amp; answers in on the live screen — independent
                  of the spoken meeting language. New meetings default to this; you can still change
                  it per meeting or live.
                </div>
              </div>
              <div className="set-field">
                <div className="set-field-top">
                  <span className="set-field-label">Comparisons in hints</span>
                </div>
                <div className="seg-lg">
                  {(
                    [
                      { value: "aggressive", label: "Frequent" },
                      { value: "conservative", label: "Only when relevant" },
                    ] as { value: ComparisonMode; label: string }[]
                  ).map((c) => (
                    <button
                      key={c.value}
                      type="button"
                      className={comparisonMode === c.value ? "on" : ""}
                      onClick={() => setComparisonMode(c.value)}
                    >
                      <span className="seg-label">{c.label}</span>
                    </button>
                  ))}
                </div>
                <div style={{ marginTop: 8, fontSize: 13, color: "var(--text-2)" }}>
                  {comparisonMode === "aggressive"
                    ? "Show a Google-vs-alternative comparison table whenever it could help — questions, product mentions, or weighing options."
                    : "Only show a comparison table when the client explicitly names a competitor or directly asks to compare products."}
                </div>
              </div>
              <div className="toggle-row" onClick={() => setAutoSummary(!autoSummary)}>
                <div>
                  <div className="toggle-row-title">Auto-generate summary</div>
                  <div className="toggle-row-sub">
                    Internal summary + client email draft within 10 seconds of meeting end.
                  </div>
                </div>
                <div className={`toggle ${autoSummary ? "on" : ""}`}>
                  <div className="toggle-knob" />
                </div>
              </div>
              <div className="toggle-row" onClick={() => setQuietByDefault(!quietByDefault)}>
                <div>
                  <div className="toggle-row-title">Start in Quiet mode</div>
                  <div className="toggle-row-sub">
                    Coach listens but only surfaces hints when you ask.
                  </div>
                </div>
                <div className={`toggle ${quietByDefault ? "on" : ""}`}>
                  <div className="toggle-knob" />
                </div>
              </div>
              <div className="toggle-row" onClick={() => setKeepTranscript(!keepTranscript)}>
                <div>
                  <div className="toggle-row-title">Keep meeting transcripts</div>
                  <div className="toggle-row-sub">
                    {keepTranscript
                      ? "Transcripts are kept after summary generation. You can delete them manually from each meeting."
                      : "Transcripts are auto-deleted after summary generation. Summaries are always kept."}
                  </div>
                </div>
                <div className={`toggle ${keepTranscript ? "on" : ""}`}>
                  <div className="toggle-knob" />
                </div>
              </div>
              <div
                className="toggle-row"
                onClick={() => setOnboardingAgentEnabled(!onboardingAgentEnabled)}
              >
                <div>
                  <div className="toggle-row-title">Onboarding voice guide</div>
                  <div className="toggle-row-sub">
                    Show the floating voice assistant that explains each screen and guides you
                    around the app. Runs on your Gemini key.
                  </div>
                </div>
                <div className={`toggle ${onboardingAgentEnabled ? "on" : ""}`}>
                  <div className="toggle-knob" />
                </div>
              </div>

              {/* Your data — GDPR/CCPA portability + erasure. */}
              <div
                style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--border-soft)" }}
              >
                <div className="toggle-row-title" style={{ marginBottom: 4 }}>
                  Your data
                </div>
                <div className="toggle-row-sub" style={{ marginBottom: 10 }}>
                  Download a copy of everything SellSynq stores about you, or permanently delete your
                  account and all your meetings, transcripts and summaries.
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={exportMyData}
                    disabled={exporting}
                  >
                    {exporting ? "Preparing…" : "Export my data"}
                  </button>
                  <button
                    type="button"
                    className="ghost-btn danger"
                    onClick={deleteMyAccount}
                    disabled={deletingAccount}
                  >
                    {deletingAccount ? "Deleting…" : "Delete my account & data"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === "summary" && (
            <div className="set-section">
              <h2>Custom summary</h2>
              <p className="set-section-sub">
                Create your own question templates — SellSynq answers them from every meeting in a
                personalized block on the summary (under Action items), falling back to Apollo.io
                for anything not discussed. Rename any question, pick a role / meeting-type set, or
                add your own named template.
              </p>

              <div className="toggle-row" onClick={() => setCustomSummaryEnabled((v) => !v)}>
                <div>
                  <div className="toggle-row-title">Enable custom summary</div>
                  <div className="toggle-row-sub">
                    {customSummaryEnabled
                      ? "The custom summary block is shown on your meeting summaries."
                      : "The custom summary block is turned off and won't be generated or shown."}
                  </div>
                </div>
                <div className={`toggle ${customSummaryEnabled ? "on" : ""}`}>
                  <div className="toggle-knob" />
                </div>
              </div>

              <SuggestedQuestionsBanner
                onAdded={(q) => setCustomQuestions((prev) => [...prev, q])}
              />

              {customSummaryEnabled && (
                <>
                  <label
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      margin: "8px 0 12px",
                    }}
                  >
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)" }}>
                      Question set
                    </span>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        className="setup-input"
                        style={{ flex: 1 }}
                        value={qSetKey}
                        onChange={(e) => {
                          const v = e.target.value;
                          setQMode("idle");
                          setQNameDraft("");
                          if (v === "__new__") {
                            setQMode("new");
                            return;
                          }
                          setQSetKey(v);
                        }}
                      >
                        <option value="">My default questions</option>
                        <optgroup label="Role / meeting-type sets">
                          {Object.keys(CUSTOM_QUESTION_PRESETS).map((k) => (
                            <option key={k} value={k}>
                              {k}
                            </option>
                          ))}
                        </optgroup>
                        {userTemplateKeys.length > 0 && (
                          <optgroup label="My templates">
                            {userTemplateKeys.map((k) => (
                              <option key={k} value={k}>
                                {k}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        <option value="__new__">+ New template…</option>
                      </select>
                      {isUserTemplate && qMode === "idle" && (
                        <>
                          <button
                            type="button"
                            className="ghost-btn"
                            onClick={() => {
                              setQMode("rename");
                              setQNameDraft(qSetKey);
                            }}
                          >
                            Rename
                          </button>
                          <button type="button" className="ghost-btn" onClick={deleteTemplate}>
                            Delete
                          </button>
                        </>
                      )}
                    </div>

                    {(qMode === "new" || qMode === "rename") && (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
                        <input
                          className="setup-input"
                          style={{ flex: 1 }}
                          autoFocus
                          placeholder={
                            qMode === "new" ? "Template name (e.g. Renewals)" : "New template name"
                          }
                          value={qNameDraft}
                          onChange={(e) => setQNameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              if (qMode === "new") createTemplate();
                              else renameTemplate();
                            }
                            if (e.key === "Escape") {
                              setQMode("idle");
                              setQNameDraft("");
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="pill-btn primary"
                          onClick={qMode === "new" ? createTemplate : renameTemplate}
                        >
                          {qMode === "new" ? "Create" : "Save name"}
                        </button>
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => {
                            setQMode("idle");
                            setQNameDraft("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                    <span style={{ fontSize: 11, color: "var(--text-4)" }}>
                      Pick a role / meeting-type set, or add your own template. Your edits are saved
                      per set and chosen per meeting at setup.
                    </span>
                  </label>
                  <CustomQuestionsEditor
                    questions={activeQuestions}
                    onChange={setActiveQuestions}
                  />
                </>
              )}

              <div className="set-callout" style={{ marginTop: 14 }}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
                <div>
                  <strong>Changes apply to new meeting summaries.</strong>
                  <div style={{ marginTop: 4 }}>
                    For an existing summary, open it and click <strong>Refresh</strong> on the
                    custom summary card to re-answer with your updated questions.
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
